namespace $ {

	/**
	 * Готовые примеры для песочницы.
	 *
	 * Пишутся так, как пишут настоящий $mol: подписи — локализуемыми строками
	 * `@ \текст`, поля — готовыми компонентами вместо самодельных, тело метода —
	 * с новой строки, а не в одну с сигнатурой. Пример учит не только тому, что
	 * в нём написано, но и тому, как это принято писать.
	 *
	 * Компоненты, на которые они ссылаются, обязаны быть в бандле: код примера
	 * компилируется в браузере и обращается к настоящим классам. В коде примеров
	 * `$` собирается через String.fromCharCode, чтобы не попадать в разбор
	 * зависимостей, поэтому перечисляем нужное здесь — в TS только `/** *\/`
	 * тащит модули в граф:
	 * $mol_view $mol_string $mol_password $mol_button_major $mol_button_minor
	 * $mol_row $mol_link $mol_check $mol_fetch $mol_state_arg $mol_state_local
	 * $mol_gap
	 */
	export type $bog_smalljs_playground_sample = {
		/** Корневой компонент примера — по нему видно, что редактор всё ещё на нём. */
		root: string
		tree: string
		ts: string
		css: string
	}

	const S = String.fromCharCode( 36 ) // "$" — вне разбора зависимостей MAM
	const lines = ( ...rows: string[] ) => rows.join( '\n' ) + '\n'

	export const $bog_smalljs_playground_samples: Record< string, $bog_smalljs_playground_sample > = {

		hello: {
			root: `${ S }my_hello`,
			// Готовый компонент вместо примитива: $mol_paragraph уже умеет
			// переносить длинный текст, а $mol_view — только коробка.
			tree: lines(
				`${ S }my_hello ${ S }mol_view`,
				`\tguest \\stranger`,
				`\tsub /`,
				`\t\t<= Name ${ S }mol_string`,
				`\t\t\tvalue? <=> name? \\mol`,
				`\t\t\thint @ \\Type a name`,
				`\t\t<= Greeting ${ S }mol_paragraph`,
				`\t\t\ttitle <= greeting \\`,
			),
			// Приветствие view.tree сам не соберёт — за него отвечает класс.
			ts: lines(
				`class ${ S }my_hello extends ${ S }.${ S }my_hello {`,
				`\tgreeting() {`,
				// Обычная строка, а не шаблонная: внутри лежит шаблонная литера
				// примера, и её `${` не должен раскрыться здесь.
				"\t\treturn `Hello, ${ this.name() || this.guest() }!`",
				`\t}`,
				`}`,
			),
			// gap стоит рядом с flex, а не внутри: вложенным он собрался бы
			// в `flex-gap`, а такого свойства в CSS нет.
			css: lines(
				`namespace ${ S } {`,
				`\t${ S }mol_style_define( ${ S }my_hello, {`,
				`\t\tgap: ${ S }mol_gap.block,`,
				`\t\tflex: { direction: 'column' },`,
				`\t\tGreeting: {`,
				`\t\t\tfont: { size: '2rem', weight: 600 },`,
				`\t\t},`,
				`\t} )`,
				`}`,
			),
		},

		counter: {
			root: `${ S }my_demo`,
			tree: lines(
				`${ S }my_demo ${ S }mol_view`,
				`\tcount_text \\0`,
				`\tinc? null`,
				`\tsub /`,
				`\t\t<= Value ${ S }mol_view`,
				`\t\t\tsub / <= count_text`,
				`\t\t<= Button ${ S }mol_button_major`,
				`\t\t\tclick? <=> inc?`,
				`\t\t\tsub / <= button_label @ \\Count up`,
			),
			ts: lines(
				`class ${ S }my_demo extends ${ S }.${ S }my_demo {`,
				``,
				`\t@ ${ S }mol_mem`,
				`\tcount( next?: number ) {`,
				`\t\treturn next ?? 0`,
				`\t}`,
				``,
				`\t@ ${ S }mol_action`,
				`\tinc() {`,
				`\t\tthis.count( this.count() + 1 )`,
				`\t}`,
				``,
				`\tcount_text() {`,
				`\t\treturn String( this.count() )`,
				`\t}`,
				``,
				`}`,
			),
			css: lines(
				`namespace ${ S } {`,
				``,
				`\t${ S }mol_style_define( ${ S }my_demo, {`,
				``,
				`\t\tflex: { direction: 'column' },`,
				`\t\tgap: '1rem',`,
				`\t\tpadding: '1.5rem',`,
				``,
				`\t\tValue: {`,
				`\t\t\tfont: { size: '2rem', weight: 700 },`,
				`\t\t\tcolor: '#0088ff',`,
				`\t\t\tpadding: { bottom: '0.5rem' },`,
				`\t\t},`,
				``,
				`\t} )`,
				``,
				`}`,
			),
		},

		fetch: {
			root: `${ S }my_fetch`,
			tree: lines(
				`${ S }my_fetch ${ S }mol_view`,
				`\trepo \\`,
				`\tstars \\`,
				`\tabout \\`,
				`\tsub /`,
				`\t\t<= Repo ${ S }mol_view`,
				`\t\t\tsub / <= repo`,
				`\t\t<= Stars ${ S }mol_view`,
				`\t\t\tsub / <= stars`,
				`\t\t<= About ${ S }mol_view`,
				`\t\t\tsub / <= about`,
			),
			// Ждать ответ не нужно: чтение подвисает, пока данные не придут,
			// а $mol сам показывает заглушку и дорисовывает по готовности.
			ts: lines(
				`class ${ S }my_fetch extends ${ S }.${ S }my_fetch {`,
				``,
				`\t@ ${ S }mol_mem`,
				`\tdata() {`,
				`\t\treturn ${ S }mol_fetch.json(`,
				`\t\t\t'https://api.github.com/repos/hyoo-ru/mam_mol'`,
				`\t\t) as { full_name: string, stargazers_count: number, description: string }`,
				`\t}`,
				``,
				`\trepo() {`,
				`\t\treturn this.data().full_name`,
				`\t}`,
				``,
				`\tstars() {`,
				`\t\treturn String( this.data().stargazers_count ) + ' stars'`,
				`\t}`,
				``,
				`\tabout() {`,
				`\t\treturn this.data().description`,
				`\t}`,
				``,
				`}`,
			),
			css: lines(
				`namespace ${ S } {`,
				``,
				`\t${ S }mol_style_define( ${ S }my_fetch, {`,
				``,
				`\t\tflex: { direction: 'column' },`,
				`\t\tgap: '0.5rem',`,
				`\t\tpadding: '1.5rem',`,
				``,
				`\t\tRepo: { font: { size: '1.25rem', weight: 700 } },`,
				`\t\tStars: { color: '#0088ff' },`,
				`\t\tAbout: { opacity: 0.7 },`,
				``,
				`\t} )`,
				``,
				`}`,
			),
		},

		args: {
			root: `${ S }my_args`,
			tree: lines(
				`${ S }my_args ${ S }mol_view`,
				`\tpage? \\home`,
				`\tgo_home? null`,
				`\tgo_about? null`,
				`\ttitle \\`,
				`\tsub /`,
				`\t\t<= Menu ${ S }mol_row`,
				`\t\t\tsub /`,
				`\t\t\t\t<= Home ${ S }mol_button_minor`,
				`\t\t\t\t\tclick? <=> go_home?`,
				`\t\t\t\t\tsub / <= home_label @ \\Home`,
				`\t\t\t\t<= About ${ S }mol_button_minor`,
				`\t\t\t\t\tclick? <=> go_about?`,
				`\t\t\t\t\tsub / <= about_label @ \\About`,
				`\t\t<= Title ${ S }mol_view`,
				`\t\t\tsub / <= title`,
			),
			// Адресная строка и есть состояние: экран переживает перезагрузку,
			// работает «назад» и ссылкой можно поделиться. Это же и роутинг.
			ts: lines(
				`class ${ S }my_args extends ${ S }.${ S }my_args {`,
				``,
				`\t@ ${ S }mol_mem`,
				`\tpage( next?: string ) {`,
				`\t\t// Own key, so the demo does not argue with the site's own arguments`,
				`\t\treturn ${ S }mol_state_arg.value( 'demo_page', next ) ?? 'home'`,
				`\t}`,
				``,
				`\t@ ${ S }mol_action`,
				`\tgo_home() {`,
				`\t\tthis.page( 'home' )`,
				`\t\treturn null`,
				`\t}`,
				``,
				`\t@ ${ S }mol_action`,
				`\tgo_about() {`,
				`\t\tthis.page( 'about' )`,
				`\t\treturn null`,
				`\t}`,
				``,
				`\ttitle() {`,
				`\t\treturn this.page() === 'about'`,
				`\t\t\t? 'About — the address bar changed too'`,
				`\t\t\t: 'Home — switch and look at the URL'`,
				`\t}`,
				``,
				`}`,
			),
			css: lines(
				`namespace ${ S } {`,
				``,
				`\t${ S }mol_style_define( ${ S }my_args, {`,
				``,
				`\t\tflex: { direction: 'column' },`,
				`\t\tgap: '1rem',`,
				`\t\tpadding: '1.5rem',`,
				``,
				`\t\tMenu: { gap: '0.5rem' },`,
				`\t\tTitle: { font: { size: '1.25rem', weight: 700 } },`,
				``,
				`\t} )`,
				``,
				`}`,
			),
		},

		routing: {
			root: `${ S }my_route`,
			tree: lines(
				`${ S }my_route ${ S }mol_view`,
				`\tscreen \\`,
				`\tsub /`,
				`\t\t<= Menu ${ S }mol_row`,
				`\t\t\tsub /`,
				`\t\t\t\t<= Home ${ S }mol_link`,
				`\t\t\t\t\targ *`,
				`\t\t\t\t\t\tdemo_route \\home`,
				`\t\t\t\t\tsub / <= home_label @ \\Home`,
				`\t\t\t\t<= Guide ${ S }mol_link`,
				`\t\t\t\t\targ *`,
				`\t\t\t\t\t\tdemo_route \\guide`,
				`\t\t\t\t\tsub / <= guide_label @ \\Guide`,
				`\t\t\t\t<= About ${ S }mol_link`,
				`\t\t\t\t\targ *`,
				`\t\t\t\t\t\tdemo_route \\about`,
				`\t\t\t\t\tsub / <= about_label @ \\About`,
				`\t\t<= Screen ${ S }mol_view`,
				`\t\t\tsub / <= screen`,
			),
			// Ссылки, а не кнопки: их можно открыть в новой вкладке, скопировать
			// и вернуться назад — роутинг в $mol достаётся от адресной строки даром.
			ts: lines(
				`class ${ S }my_route extends ${ S }.${ S }my_route {`,
				``,
				`\t@ ${ S }mol_mem`,
				`\troute() {`,
				`\t\t// Own key, so the demo does not argue with the site's own arguments`,
				`\t\treturn ${ S }mol_state_arg.value( 'demo_route' ) ?? 'home'`,
				`\t}`,
				``,
				`\tscreen() {`,
				`\t\tconst route = this.route()`,
				`\t\tif( route === 'guide' ) return 'Guide — press Back and the previous screen returns'`,
				`\t\tif( route === 'about' ) return 'About — a link is an address, so it opens in a new tab too'`,
				`\t\treturn 'Home — pick a link and watch the address bar'`,
				`\t}`,
				``,
				`}`,
			),
			css: lines(
				`namespace ${ S } {`,
				``,
				`\t${ S }mol_style_define( ${ S }my_route, {`,
				``,
				`\t\tflex: { direction: 'column' },`,
				`\t\tgap: '1rem',`,
				`\t\tpadding: '1.5rem',`,
				``,
				`\t\tMenu: { gap: '1rem' },`,
				`\t\tScreen: { font: { size: '1.125rem' } },`,
				``,
				`\t} )`,
				``,
				`}`,
			),
		},

		state: {
			root: `${ S }my_state`,
			tree: lines(
				`${ S }my_state ${ S }mol_view`,
				`\tnote? \\`,
				`\tsub /`,
				`\t\t<= Hint ${ S }mol_view`,
				`\t\t\tsub / <= hint_text @ \\Type something, then reload the page`,
				`\t\t<= Note ${ S }mol_string`,
				`\t\t\tvalue? <=> note?`,
				`\t\t\thint @ \\It survives a reload`,
			),
			// Единственное отличие от обычного поля — где лежит значение.
			ts: lines(
				`class ${ S }my_state extends ${ S }.${ S }my_state {`,
				``,
				`\t@ ${ S }mol_mem`,
				`\tnote( next?: string ) {`,
				`\t\treturn ${ S }mol_state_local.value( 'my_demo_note', next ) ?? ''`,
				`\t}`,
				``,
				`}`,
			),
			css: lines(
				`namespace ${ S } {`,
				``,
				`\t${ S }mol_style_define( ${ S }my_state, {`,
				``,
				`\t\tflex: { direction: 'column' },`,
				`\t\tgap: '0.75rem',`,
				`\t\tpadding: '1.5rem',`,
				``,
				`\t\tHint: { opacity: 0.7 },`,
				``,
				`\t} )`,
				``,
				`}`,
			),
		},

		login: {
			root: `${ S }my_login`,
			tree: lines(
				`${ S }my_login ${ S }mol_view`,
				`\tmail? \\`,
				`\tpass? \\`,
				`\thint \\`,
				`\tvalid false`,
				`\tsubmit? null`,
				`\tsub /`,
				`\t\t<= Mail ${ S }mol_string`,
				`\t\t\tvalue? <=> mail?`,
				`\t\t\thint @ \\E-mail`,
				// Готовый компонент вместо `$mol_string` с `type \password`:
				// в нём уже есть кнопка «показать пароль» со своей иконкой.
				`\t\t<= Pass ${ S }mol_password`,
				`\t\t\tvalue? <=> pass?`,
				`\t\t\thint @ \\Password`,
				`\t\t<= Hint_text ${ S }mol_view`,
				`\t\t\tsub / <= hint`,
				`\t\t<= Send ${ S }mol_button_major`,
				`\t\t\tenabled <= valid`,
				`\t\t\tclick? <=> submit?`,
				`\t\t\tsub / <= send_label @ \\Sign in`,
			),
			// Кнопка и подсказка ничего не хранят — они пересчитываются из полей.
			ts: lines(
				`class ${ S }my_login extends ${ S }.${ S }my_login {`,
				``,
				`\tmail_ok() {`,
				`\t\treturn /.+@.+\\..+/.test( this.mail() )`,
				`\t}`,
				``,
				`\tpass_ok() {`,
				`\t\treturn this.pass().length >= 8`,
				`\t}`,
				``,
				`\tvalid() {`,
				`\t\treturn this.mail_ok() && this.pass_ok()`,
				`\t}`,
				``,
				`\t@ ${ S }mol_mem`,
				`\tsent( next?: boolean ) {`,
				`\t\treturn next ?? false`,
				`\t}`,
				``,
				`\t@ ${ S }mol_action`,
				`\tsubmit() {`,
				`\t\tthis.sent( true )`,
				`\t\treturn null`,
				`\t}`,
				``,
				`\thint() {`,
				`\t\tif( this.sent() ) return 'Signed in as ' + this.mail()`,
				`\t\tif( !this.mail() && !this.pass() ) return 'Fill both fields'`,
				`\t\tif( !this.mail_ok() ) return 'This e-mail looks wrong'`,
				`\t\tif( !this.pass_ok() ) return 'Password: 8 characters or more'`,
				`\t\treturn 'Looks good'`,
				`\t}`,
				``,
				`}`,
			),
			css: lines(
				`namespace ${ S } {`,
				``,
				`\t${ S }mol_style_define( ${ S }my_login, {`,
				``,
				`\t\tflex: { direction: 'column' },`,
				`\t\tgap: '0.75rem',`,
				`\t\tpadding: '1.5rem',`,
				`\t\tmaxWidth: '20rem',`,
				``,
				`\t\tHint_text: { opacity: 0.7, font: { size: '0.875rem' } },`,
				``,
				`\t} )`,
				``,
				`}`,
			),
		},

	}

	/** Порядок в выпадашке. */
	export const $bog_smalljs_playground_sample_ids = [ 'hello', 'counter', 'fetch', 'args', 'routing', 'state', 'login' ]

}
